import type { AppRole } from "./enums.js";

export const ROLE_LABELS: Record<AppRole, string> = {
  super_admin: "Super Administrator",
  somasi_owner: "Somasi Owner",
  somasi_manager: "Somasi Manager",
  somasi_team: "Somasi Team Member",
  outsourced_admin: "Outsourced Admin",
  outsourced_team: "Outsourced Team Member",
  readonly: "Read-only",
};

/** Roles that may write master data — the old `*_write_admin` RLS policies. */
export const EDIT_ROLES: AppRole[] = [
  "super_admin",
  "outsourced_admin",
  "somasi_owner",
  "somasi_manager",
];

/** Roles with administrative reach — the old `audit_select_admin` / user-role policies. */
export const ADMIN_ROLES: AppRole[] = ["super_admin", "outsourced_admin"];

/** Roles that may log delivered work and expenses (`sev_write` / `sun_write`). */
export const WORK_LOG_ROLES: AppRole[] = [
  "super_admin",
  "outsourced_admin",
  "somasi_owner",
  "somasi_manager",
  "somasi_team",
  "outsourced_team",
];

/** Roles that may reconcile the VIRRGIN account (`vt_write`). */
export const VIRRGIN_ROLES: AppRole[] = [
  "super_admin",
  "outsourced_admin",
  "somasi_owner",
  "somasi_manager",
  "outsourced_team",
];

/** Roles that may read bank account details (`ba_select`). */
export const BANK_READ_ROLES: AppRole[] = EDIT_ROLES;

export function hasAnyRole(roles: readonly AppRole[], allowed: readonly AppRole[]): boolean {
  return allowed.some((r) => roles.includes(r));
}

/** `private.is_staff()` — any assigned role makes a user staff. */
export function isStaff(roles: readonly AppRole[]): boolean {
  return roles.length > 0;
}

/**
 * Roles that may see what people are paid.
 *
 * Narrower than `BANK_READ_ROLES`, which already limits client bank details to
 * the four management roles: a colleague's salary is the most sensitive data in
 * the system, and a manager who approves attendance has no need of it. Anyone
 * may always read their own payslip — that is the registry's `read: "self"`, not
 * a role.
 */
export const PAYROLL_READ_ROLES: AppRole[] = ["super_admin", "somasi_owner", "outsourced_admin"];

/** Roles that may change a contract, a rate, or run and approve the payroll. */
export const PAYROLL_WRITE_ROLES: AppRole[] = ["super_admin", "somasi_owner"];

/**
 * Roles that may approve leave and settle attendance.
 *
 * Deliberately wider than the two above and deliberately separate: approving a
 * day off is a line-management act, and it should not require being trusted with
 * the payroll.
 */
export const PAYROLL_APPROVE_ROLES: AppRole[] = [
  "super_admin",
  "somasi_owner",
  "somasi_manager",
  "outsourced_admin",
];

/** Roles that may record attendance and request leave — everyone with a role. */
export const ATTENDANCE_WRITE_ROLES: AppRole[] = [
  "super_admin",
  "outsourced_admin",
  "somasi_owner",
  "somasi_manager",
  "somasi_team",
  "outsourced_team",
];
